import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageMetadata from '../utils/usePageMetadata';
import './FeaturePage.css';

const ContentFeatures = () => {
  const { token } = useAuth();

  usePageMetadata({
    title: 'Content Dashboard | Page Performance & Content Analytics',
    description:
      'Understand which content performs best and optimize your publishing strategy. Track page performance, social media engagement, and content insights.',
    canonicalUrl: 'https://theadshop.co/content-features',
    meta: [
      { property: 'og:title', content: 'Content Dashboard | Page Performance & Content Analytics' },
      {
        property: 'og:description',
        content:
          'Track page-level performance, RSS feed integration, social media engagement, and content insights to optimize your publishing strategy.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://theadshop.co/content-features' },
      { property: 'og:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:title', content: 'Content Dashboard | Page Performance & Content Analytics' },
      {
        name: 'twitter:description',
        content:
          'Discover which content drives traffic, engagement, and revenue with detailed page-level analytics and social media tracking.',
      },
    ],
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Content Dashboard Features | The Ad Shop',
        url: 'https://theadshop.co/content-features',
        description:
          'Discover The Ad Shop Content Dashboard features including page-level performance analytics, RSS feed integration, social media tracking, and content optimization insights.',
        isPartOf: {
          '@type': 'WebSite',
          name: 'The Ad Shop',
          url: 'https://theadshop.co/',
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://theadshop.co/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Content Dashboard',
              item: 'https://theadshop.co/content-features',
            },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'The Ad Shop Content Dashboard',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0.00',
          priceCurrency: 'USD',
        },
        featureList: [
          'Page-level performance analytics',
          'RSS feed integration and content discovery',
          'Social media platform tracking',
          'Content insights and optimization recommendations',
          'Category and section performance breakdowns',
          'Trending content identification',
        ],
        description:
          'Content performance dashboard that helps publishers understand which content performs best, track social media engagement, and optimize publishing strategy.',
      },
    ],
  });

  return (
    <div className="feature-page">
      <div className="feature-container">
        <div className="feature-header">
          <div className="feature-icon-large">📝</div>
          <h1>Content Dashboard</h1>
          <p className="feature-subtitle">
            Understand which content performs best and optimize your publishing strategy. Track page performance, 
            social media engagement, and content insights.
          </p>
        </div>

        <div className="feature-content">
          <section className="feature-section">
            <h2>Page-Level Performance Analytics</h2>
            <p>
              Dive deep into individual page performance with comprehensive metrics for every piece of content:
            </p>
            <ul className="feature-list">
              <li><strong>Traffic Metrics:</strong> Pageviews, users, sessions, and engagement rates for each page</li>
              <li><strong>Engagement Analysis:</strong> Average engagement time, bounce rate, and engagement rate per page</li>
              <li><strong>Revenue Tracking:</strong> AdSense earnings and RPM for each individual page</li>
              <li><strong>Search Performance:</strong> Search Console data including impressions, clicks, CTR, and average position</li>
              <li><strong>Trend Analysis:</strong> Compare current performance to previous periods to identify growth or decline</li>
            </ul>
            <p>
              Sort and filter pages by any metric to quickly identify your best and worst performing content.
            </p>
          </section>

          <section className="feature-section">
            <h2>RSS Feed Integration & Content Discovery</h2>
            <p>
              Automatically discover and track new content through RSS feed integration:
            </p>
            <ul className="feature-list">
              <li><strong>RSS Feed Monitoring:</strong> Connect your RSS feed to automatically discover new pages as they're published</li>
              <li><strong>Automatic Content Detection:</strong> New posts are automatically added to your dashboard</li>
              <li><strong>Content Categorization:</strong> Organize content by categories and sections for better analysis</li>
              <li><strong>Publishing Insights:</strong> Track which categories and sections drive the most traffic and engagement</li>
            </ul>
            <p>
              Never miss tracking a new piece of content. Once your RSS feed is connected, new posts are automatically 
              discovered and added to your analytics.
            </p>
          </section>

          <section className="feature-section">
            <h2>Social Media Platform Tracking</h2>
            <p>
              Track how your content performs across major social media platforms:
            </p>
            <div className="platforms-grid">
              <div className="platform-item">Facebook</div>
              <div className="platform-item">Twitter / X</div>
              <div className="platform-item">Instagram</div>
              <div className="platform-item">YouTube</div>
              <div className="platform-item">TikTok</div>
              <div className="platform-item">LinkedIn</div>
              <div className="platform-item">Pinterest</div>
              <div className="platform-item">Reddit</div>
              <div className="platform-item">Threads</div>
              <div className="platform-item">Bluesky</div>
              <div className="platform-item">Mastodon</div>
              <div className="platform-item">Snapchat</div>
            </div>
            <p>
              See which platforms drive the most traffic to each page, understand engagement patterns across platforms, 
              and identify opportunities to expand your social media presence.
            </p>
          </section>

          <section className="feature-section">
            <h2>Content Insights & Optimization</h2>
            <p>
              Get actionable insights to improve your content strategy:
            </p>
            <ul className="feature-list">
              <li><strong>Trending Content:</strong> Identify pages with rapid growth (50%+ increase) to replicate successful content</li>
              <li><strong>High Engagement, Low Traffic:</strong> Find great content that needs promotion via social media or internal linking</li>
              <li><strong>Declining Performance:</strong> Get alerts when content experiences traffic drops to check for indexing or freshness issues</li>
              <li><strong>Engagement Mismatch:</strong> Discover pages with high time-on-page but low engagement rates that need CTAs</li>
              <li><strong>Revenue Opportunities:</strong> Identify high-traffic pages with low revenue potential for optimization</li>
            </ul>
            <p>
              Each insight includes specific recommendations and impact scores to help you prioritize your content optimization efforts.
            </p>
          </section>

          <section className="feature-section">
            <h2>Category & Section Performance</h2>
            <p>
              Understand which types of content drive the most value:
            </p>
            <ul className="feature-list">
              <li><strong>Category Breakdowns:</strong> See performance metrics aggregated by content category</li>
              <li><strong>Section Analysis:</strong> Track which sections of your site perform best</li>
              <li><strong>Content Type Comparison:</strong> Compare performance across different content types</li>
              <li><strong>Publishing Patterns:</strong> Identify optimal publishing schedules and content mix</li>
            </ul>
            <p>
              Use category and section filters to drill down into specific content areas and understand what resonates 
              most with your audience.
            </p>
          </section>

          <section className="feature-section">
            <h2>Advanced Filtering & Date Ranges</h2>
            <p>
              Analyze content performance across different time periods and filters:
            </p>
            <ul className="feature-list">
              <li><strong>Custom Date Ranges:</strong> Analyze any time period from the past</li>
              <li><strong>Quick Presets:</strong> 7-day, 14-day, 30-day, and custom range options</li>
              <li><strong>Section Filtering:</strong> Focus on specific sections or categories</li>
              <li><strong>Sort Options:</strong> Sort by pageviews, engagement, revenue, or any other metric</li>
              <li><strong>Search Functionality:</strong> Quickly find specific pages or content</li>
            </ul>
            <p>
              Combine filters to answer specific questions like "Which tech articles drove the most revenue last month?" 
              or "What content performed best on mobile devices?"
            </p>
          </section>

          <section className="feature-section">
            <h2>Individual Page Deep Dives</h2>
            <p>
              Click into any page for a detailed performance breakdown:
            </p>
            <ul className="feature-list">
              <li>Complete traffic and engagement metrics</li>
              <li>Revenue and RPM analysis</li>
              <li>Search Console performance data</li>
              <li>Social media referral breakdown</li>
              <li>Device and geographic distribution</li>
              <li>Historical performance trends</li>
              <li>Actionable insights specific to that page</li>
            </ul>
            <p>
              Get everything you need to understand why a page is performing well or poorly, and what you can do to improve it.
            </p>
          </section>

          <section className="feature-cta">
            <h2>Ready to Optimize Your Content?</h2>
            <p>
              Start tracking page performance, social engagement, and content insights today.
            </p>
            {token ? (
              <Link to="/content" className="btn-primary">
                Go to Content Dashboard
              </Link>
            ) : (
              <Link to="/signup" className="btn-primary">
                Sign Up Free
              </Link>
            )}
            <p className="cta-note">Free to get started • No credit card required • Setup in minutes</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ContentFeatures;

